import { ChevronLeftIcon, ShoppingCartIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { SubmitButton } from "@/components/buttons/submit-button";
import { LoadingModal } from "@/components/modals/loading-modal";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";

import { useSession } from "@/hooks/use-session";
import { formatCurrency } from "@/lib/utils";
import {
  useGetOrderDetailsQuery,
  usePayWithWalletMutation,
} from "@/redux/services/order-portal";

export default function PayWithWallet() {
  const { id } = useParams();
  const { session } = useSession();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { data: orderDetails, isLoading: isLoadingOrderDetails } =
    useGetOrderDetailsQuery({
      id: id ?? "",
    });

  const [payWithWallet, { isLoading }] = usePayWithWalletMutation();

  if (!id) {
    return <Navigate to="/" />;
  }

  const handlePayWithWallet = async () => {
    try {
      if (!(session?.token && id)) {
        return;
      }

      const payWithWalletResponse = await payWithWallet({
        loginToken: session.token,
        id,
      });
      if (payWithWalletResponse.error) {
        throw new Error("Failed to pay with wallet");
      }

      navigate(`/receipt/${id}`);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "An unknown error occurred"
      );
    }
  };

  return (
    <>
      {isLoadingOrderDetails && <LoadingModal isOpen={isLoadingOrderDetails} />}

      <section className="space-y-6 py-6">
        <h1 className="text-pretty text-center text-2xl text-muted-foreground">
          {t("pay-via-wallet.title")}
        </h1>

        <Card className="mx-auto max-w-2xl">
          <CardContent className="flex items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <ShoppingCartIcon className="size-5" />
              <p className="font-semibold">
                {orderDetails && "customerType" in orderDetails
                  ? orderDetails.packageName
                  : (orderDetails?.order_details[0].bundle_name ?? "")}
              </p>
            </div>
            <div className="font-semibold text-destructive text-lg">
              {formatCurrency(
                orderDetails && "amount" in orderDetails
                  ? orderDetails.amount
                  : Number(orderDetails?.total ?? 0)
              )}
            </div>
          </CardContent>
          <CardFooter className="flex flex-col-reverse gap-4 sm:flex-row">
            <Button
              className="w-full sm:w-1/4"
              onClick={() => navigate(-1)}
              variant="outline"
            >
              <ChevronLeftIcon />
              <span>{t("labels.back")}</span>
            </Button>
            <SubmitButton
              className="w-full sm:w-3/4"
              isSubmitting={isLoading}
              onClick={handlePayWithWallet}
            >
              {t("labels.pay-with-wallet")}
            </SubmitButton>
          </CardFooter>
        </Card>
      </section>
    </>
  );
}
